import { useEffect, useState, type ReactNode } from 'react';
import { probeSession } from '@/lib/auth';

interface AuthGateProps {
  children: ReactNode;
}

type AuthState = 'checking' | 'ok';

// AuthGate is intentionally fail-safe: Cloudflare Access intercepts unauthenticated
// requests at the edge and serves its own challenge UI before the SPA ever loads,
// so by the time this component runs we know the user is past Access (or Access
// is not configured for this hostname). The session probe is best-effort —
// if it fails we still render the app instead of bouncing to a client-side
// login path that doesn't exist.
export function AuthGate({ children }: AuthGateProps) {
  const [state, setState] = useState<AuthState>('checking');

  useEffect(() => {
    let cancelled = false;
    void probeSession()
      .catch(() => false)
      .then((ok) => {
        if (cancelled) return;
        if (!ok) {
          // Backend session not validated — could be CF Access not yet
          // configured, or API base not reachable. Render anyway so the
          // shell is usable; individual API calls will surface their own
          // errors.
          console.warn('[AuthGate] session probe failed — rendering shell anyway');
        }
        setState('ok');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-50 text-ink-500 text-sm dark:bg-[#0B1220] dark:text-[#94A3B8]">
        Loading…
      </div>
    );
  }
  return <>{children}</>;
}
